import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, MapPin, Download, Pencil, Send, FileEdit, XCircle, Receipt, Upload, ExternalLink, Truck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { formatRupiah, formatDateTime, statusClass, alamatTujuanPO } from "../lib/format";
import { PhoneInput, isValidPhone } from "../components/UI";
import { compressImageFile, formatUkuran } from "../lib/imageCompress";

const FLOW = {
  "Draft PO": ["PO Terkirim", "Dibatalkan"],
  "PO Terkirim": ["PO Diproses", "Dibatalkan"],
  "PO Diproses": ["Dalam Pengiriman", "Dibatalkan"],
  "Dalam Pengiriman": ["Barang Diterima", "Dibatalkan"],
  "Barang Diterima": ["Selesai"],
  Selesai: [],
  Dibatalkan: [],
};

// Label tombol aksi per status tujuan.
const ACTION_LABEL = {
  "PO Terkirim": "Kirim ke Supplier",
  "PO Diproses": "Proses PO",
  "Dalam Pengiriman": "Dalam Pengiriman",
  "Barang Diterima": "Barang Diterima",
  Selesai: "Konfirmasi Selesai",
  Dibatalkan: "Batal",
};

const KONFIRMASI = {
  "PO Terkirim": "Kirim PO ini ke supplier? Setelah terkirim, isi PO tidak dapat diubah lagi.",
  "PO Diproses": "Mulai memproses PO ini?",
  "Dalam Pengiriman": "Tandai pesanan sedang dalam pengiriman?",
  "Barang Diterima": "Konfirmasi bahwa barang sudah diterima lengkap?",
  Selesai: "Tutup transaksi ini? Status PO menjadi Selesai.",
};

export default function PurchaseOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [po, setPo] = useState(null);
  const [error, setError] = useState("");
  const [kirimOpen, setKirimOpen] = useState(false);
  const [pengirim, setPengirim] = useState({ nama: "", telp: "" });
  const [kirimError, setKirimError] = useState("");
  const [mengirim, setMengirim] = useState(false);
  const [batalOpen, setBatalOpen] = useState(false);
  const [alasan, setAlasan] = useState("");
  const [batalError, setBatalError] = useState("");
  const [membatalkan, setMembatalkan] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/purchase-orders/${id}`);
      setPo(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const updateStatus = async (status) => {
    // Pembatalan selalu lewat modal agar alasannya tercatat.
    if (status === "Dibatalkan") {
      setAlasan("");
      setBatalError("");
      setBatalOpen(true);
      return;
    }
    // Pengiriman memerlukan identitas pembawa barang.
    if (status === "Dalam Pengiriman") {
      setPengirim({ nama: po?.pengirim_nama || "", telp: po?.pengirim_telp || "" });
      setKirimError("");
      setKirimOpen(true);
      return;
    }
    if (!window.confirm(KONFIRMASI[status] || `Ubah status menjadi "${status}"?`)) return;
    try {
      await api.patch(`/purchase-orders/${id}/status`, { status });
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const submitKirim = async (e) => {
    e.preventDefault();
    if (!pengirim.nama.trim()) {
      setKirimError("Nama pengirim/sopir wajib diisi.");
      return;
    }
    if (!isValidPhone(pengirim.telp)) {
      setKirimError("No. telepon/WA tidak valid. Masukkan minimal 8 angka setelah +62.");
      return;
    }
    setMengirim(true);
    try {
      await api.patch(`/purchase-orders/${id}/status`, {
        status: "Dalam Pengiriman",
        pengirim_nama: pengirim.nama.trim(),
        pengirim_telp: pengirim.telp,
      });
      setKirimOpen(false);
      await load();
    } catch (err) {
      setKirimError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setMengirim(false); }
  };

  const submitBatal = async (e) => {
    e.preventDefault();
    if (!alasan.trim()) {
      setBatalError("Alasan pembatalan wajib diisi.");
      return;
    }
    setMembatalkan(true);
    try {
      await api.patch(`/purchase-orders/${id}/status`, { status: "Dibatalkan", alasan: alasan.trim() });
      setBatalOpen(false);
      await load();
    } catch (err) {
      setBatalError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setMembatalkan(false); }
  };

  if (error) return <div className="kedan-card p-6 text-red-700" data-testid="po-error">{error}</div>;
  if (!po) return <div className="text-slate-500 text-sm">Memuat...</div>;

  const isDraft = po.status === "Draft PO";
  const canEditDraft = isDraft && (user.role === "KPPG" || (user.role === "SPPG" && po.sppg_user_id === user.id));
  const allowed = FLOW[po.status] || [];
  const supplierTransitions = new Set(["PO Diproses", "Dalam Pengiriman", "Selesai", "Dibatalkan"]);
  const sppgTransitions = new Set(["PO Terkirim", "Barang Diterima", "Dibatalkan"]);
  const isSppgPemilik = user.role === "SPPG" && po.sppg_user_id === user.id;
  const canTransition = (t) => {
    if (user.role === "KPPG") return true;
    if (user.role === "Supplier") return supplierTransitions.has(t);
    if (user.role === "SPPG") {
      // Saat barang sudah berjalan, pembatalan menjadi hak supplier.
      if (t === "Dibatalkan" && po.status === "Dalam Pengiriman") return false;
      return sppgTransitions.has(t) && isSppgPemilik;
    }
    return false;
  };
  // SPPG tetap melihat tombol Batal saat pengiriman, tetapi nonaktif —
  // ditampilkan untuk seluruh user role SPPG, bukan hanya pembuat PO.
  const batalNonaktif = user.role === "SPPG" && po.status === "Dalam Pengiriman";
  // Bukti pembayaran diunggah SPPG pembuat PO, selama PO sudah terkirim dan belum dibatalkan.
  const bisaUnggahBukti =
    (user.role === "KPPG" || isSppgPemilik) &&
    !["Draft PO", "Dibatalkan"].includes(po.status);

  return (
    <div className="space-y-6" data-testid="po-detail-page">
      {/* ── Screen-only view ── */}
      <div className="print:hidden space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Kembali
          </button>
          <button
            onClick={() => window.print()}
            disabled={isDraft}
            title={isDraft ? "PO masih berupa draft — kirim ke supplier terlebih dahulu" : undefined}
            className={isDraft
              ? "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
              : "kedan-btn-secondary"}
            data-testid="export-pdf-btn"
          >
            <Download className="w-4 h-4" /> Ekspor PDF
          </button>
        </div>

        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="kedan-label text-slate-500">Purchase Order</div>
            <h1 className="text-2xl font-display font-bold text-slate-900 mt-1 font-mono">{po.po_number}</h1>
            <div className="mt-2"><span className={`kedan-badge ${statusClass(po.status)}`}>{po.status}</span></div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canEditDraft && (
              <button onClick={() => navigate(`/po-edit/${po.id}`)} className="kedan-btn-secondary" data-testid="edit-draft-btn">
                <Pencil className="w-4 h-4" /> Ubah Draft
              </button>
            )}
            {allowed.filter(canTransition).map((s) => (
              <button key={s} onClick={() => updateStatus(s)}
                className={s === "Dibatalkan" ? "kedan-btn-danger" : "kedan-btn-primary"}
                data-testid={`status-action-${s.replace(/\s+/g, "-")}`}>
                {s === "Dibatalkan" ? ACTION_LABEL[s]
                  : s === "PO Terkirim" ? <>{ACTION_LABEL[s]} <Send className="w-4 h-4" /></>
                  : <>{ACTION_LABEL[s] || s} <ChevronRight className="w-4 h-4" /></>}
              </button>
            ))}
            {batalNonaktif && (
              <button
                type="button"
                disabled
                title="PO yang sedang dalam pengiriman hanya dapat dibatalkan oleh supplier"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
                data-testid="status-action-Dibatalkan-disabled"
              >
                Batal
              </button>
            )}
          </div>
        </div>

        {isDraft && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2" data-testid="draft-notice">
            <FileEdit className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-800">
              PO ini masih berupa <span className="font-semibold">draft</span> — isinya masih bisa diubah dan
              belum terlihat oleh supplier. Tekan <span className="font-semibold">Kirim ke Supplier</span> bila sudah final.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
          <div className="kedan-card p-5 space-y-3">
            <div className="kedan-label text-slate-500">Informasi</div>
            <Detail label="SPPG" value={po.sppg_name} />
            <Detail label="Supplier" value={po.supplier_nama} />
            <Detail label="Dibuat" value={formatDateTime(po.created_at)} />
            <Detail label="Update Terakhir" value={formatDateTime(po.updated_at)} />
            {(po.dest_kota || po.dest_alamat) && (
              <div data-testid="info-tujuan">
                <div className="text-xs text-slate-500">Tujuan Pengiriman</div>
                <div className="mt-0.5 flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#045498] mt-0.5 shrink-0" />
                  <div className="text-sm">
                    {po.dest_instansi && <div className="font-medium text-slate-800">{po.dest_instansi}</div>}
                    <div className="text-slate-600 leading-relaxed">{alamatTujuanPO(po) || po.dest_kota}</div>
                  </div>
                </div>
              </div>
            )}
            {po.dest_pic && (
              <Detail
                label="PIC SPPG"
                value={po.dest_pic_jabatan ? `${po.dest_pic} · ${po.dest_pic_jabatan}` : po.dest_pic}
              />
            )}
            {po.dest_telp && (
              <Detail
                label="No. Telp SPPG"
                value={<a href={`tel:${po.dest_telp}`} className="text-[#045498] hover:underline">{po.dest_telp}</a>}
              />
            )}
            {po.catatan && <Detail label="Catatan Pesanan" value={po.catatan} />}
            {po.pengirim_nama && (
              <div className="pt-3 border-t border-slate-200 space-y-3" data-testid="info-pengirim">
                <div>
                  <div className="text-xs text-slate-500">Pengirim / Sopir</div>
                  <div className="mt-0.5 flex items-start gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-[#045498] mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <div className="font-medium text-slate-800">{po.pengirim_nama}</div>
                      {po.pengirim_telp && (
                        <a href={`tel:${po.pengirim_telp}`} className="text-[#045498] hover:underline">{po.pengirim_telp}</a>
                      )}
                    </div>
                  </div>
                </div>
                {po.dikirim_pada && <Detail label="Waktu Pengiriman" value={formatDateTime(po.dikirim_pada)} />}
              </div>
            )}
            {po.status === "Dibatalkan" && (
              <div className="pt-3 border-t border-slate-200 space-y-3" data-testid="info-pembatalan">
                <Detail label="Alasan Pembatalan" value={po.alasan_batal || "-"} />
                <Detail label="Dibatalkan Pada" value={formatDateTime(po.dibatalkan_pada || po.updated_at)} />
                {po.dibatalkan_oleh && <Detail label="Dibatalkan Oleh" value={po.dibatalkan_oleh} />}
              </div>
            )}
          </div>

          <BuktiPembayaran po={po} canUpload={bisaUnggahBukti} onChanged={load} />
          </div>

          <div className="kedan-card overflow-hidden lg:col-span-2">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="kedan-label text-slate-500">Detail Item</div>
                <div className="font-display font-semibold">Komoditas Dipesan</div>
              </div>
              <div className="text-right">
                <div className="kedan-label text-slate-500">Total Nilai</div>
                <div className="font-display text-xl font-bold text-[#045498]">{formatRupiah(po.total)}</div>
              </div>
            </div>
            <table className="kedan-table">
              <thead>
                <tr>
                  <th>Produk</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Harga Satuan</th>
                  <th className="text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {po.items.map((it, i) => (
                  <tr key={i}>
                    <td className="font-medium">{it.product_nama}</td>
                    <td className="text-right font-mono">{it.qty}</td>
                    <td className="text-right font-mono">{formatRupiah(it.harga)}</td>
                    <td className="text-right font-mono font-semibold">{formatRupiah(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {kirimOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4 print:hidden" data-testid="modal-pengiriman">
          <form onSubmit={submitKirim} className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-start gap-2">
              <Truck className="w-5 h-5 text-[#045498] mt-0.5 shrink-0" />
              <div>
                <h2 className="font-display font-semibold text-slate-900">Data Pengiriman {po.po_number}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Isi identitas pembawa barang agar SPPG dapat menghubungi saat pesanan tiba.
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Nama Pengirim / Sopir</label>
              <input
                className="kedan-input"
                value={pengirim.nama}
                onChange={(e) => { setPengirim((p) => ({ ...p, nama: e.target.value })); setKirimError(""); }}
                placeholder="Contoh: Andi Saputra"
                autoFocus
                data-testid="input-pengirim-nama"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">No. Telp / WA</label>
              <PhoneInput
                value={pengirim.telp}
                onChange={(v) => { setPengirim((p) => ({ ...p, telp: v })); setKirimError(""); }}
                testid="input-pengirim-telp"
              />
              <p className="text-xs text-slate-400 mt-1">Hanya angka, tanpa 0 di depan. Disimpan sebagai +62…</p>
            </div>
            {kirimError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="pengiriman-error">{kirimError}</div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setKirimOpen(false)} className="kedan-btn-secondary" data-testid="pengiriman-tutup">
                Batal
              </button>
              <button type="submit" disabled={mengirim} className="kedan-btn-primary" data-testid="pengiriman-submit">
                {mengirim ? "Menyimpan..." : "Mulai Pengiriman"}
              </button>
            </div>
          </form>
        </div>
      )}

      {batalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4 print:hidden" data-testid="modal-batal">
          <form onSubmit={submitBatal} className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <h2 className="font-display font-semibold text-slate-900">Batalkan {po.po_number}?</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Stok produk akan dikembalikan. Alasan pembatalan dicatat dan ditampilkan pada daftar PO.
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Alasan Pembatalan</label>
              <textarea
                rows="3"
                className="kedan-input"
                value={alasan}
                onChange={(e) => { setAlasan(e.target.value); setBatalError(""); }}
                placeholder="Contoh: stok supplier tidak mencukupi"
                autoFocus
                data-testid="input-alasan-batal"
              />
            </div>
            {batalError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2" data-testid="batal-error">{batalError}</div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setBatalOpen(false)} className="kedan-btn-secondary" data-testid="batal-tutup">
                Kembali
              </button>
              <button type="submit" disabled={membatalkan} className="kedan-btn-danger" data-testid="batal-submit">
                {membatalkan ? "Membatalkan..." : "Batalkan PO"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Print-only view: clean PDF layout ── */}
      <div className="hidden print:block">
        <h1 style={{ fontSize: "18px", fontWeight: 700, marginBottom: 4 }}>Purchase Order — {po.po_number}</h1>
        <p style={{ fontSize: "11px", color: "#64748B", marginBottom: 16 }}>Diekspor pada: {new Date().toLocaleDateString("id-ID", { dateStyle: "long" })}</p>

        <table style={{ fontSize: "11px", marginBottom: 16, borderCollapse: "collapse" }}>
          <tbody>
            <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Status</td><td style={{ padding: "3px 0", fontWeight: 600 }}>{po.status}</td></tr>
            <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>SPPG</td><td style={{ padding: "3px 0" }}>{po.sppg_name}</td></tr>
            <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Supplier</td><td style={{ padding: "3px 0" }}>{po.supplier_nama}</td></tr>
            <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Tanggal Dibuat</td><td style={{ padding: "3px 0" }}>{formatDateTime(po.created_at)}</td></tr>
            {po.dest_instansi && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Tujuan</td><td style={{ padding: "3px 0" }}>{po.dest_instansi}</td></tr>}
            {(po.dest_alamat || po.dest_kota) && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B", verticalAlign: "top" }}>Alamat Tujuan</td><td style={{ padding: "3px 0" }}>{alamatTujuanPO(po) || po.dest_kota}</td></tr>}
            {po.dest_pic && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>PIC SPPG</td><td style={{ padding: "3px 0" }}>{po.dest_pic_jabatan ? `${po.dest_pic} · ${po.dest_pic_jabatan}` : po.dest_pic}</td></tr>}
            {po.dest_telp && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>No. Telp SPPG</td><td style={{ padding: "3px 0" }}>{po.dest_telp}</td></tr>}
            {po.pengirim_nama && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Pengirim / Sopir</td><td style={{ padding: "3px 0" }}>{po.pengirim_nama}{po.pengirim_telp ? ` · ${po.pengirim_telp}` : ""}</td></tr>}
            {po.catatan && <tr><td style={{ padding: "3px 12px 3px 0", color: "#64748B" }}>Catatan Pesanan</td><td style={{ padding: "3px 0" }}>{po.catatan}</td></tr>}
          </tbody>
        </table>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #334155" }}>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>No</th>
              <th style={{ textAlign: "left", padding: "6px 8px" }}>Produk</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>Qty</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>Harga Satuan</th>
              <th style={{ textAlign: "right", padding: "6px 8px" }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {po.items.map((it, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #E2E8F0" }}>
                <td style={{ padding: "5px 8px" }}>{i + 1}</td>
                <td style={{ padding: "5px 8px" }}>{it.product_nama}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>{it.qty}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace" }}>{formatRupiah(it.harga)}</td>
                <td style={{ padding: "5px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 600 }}>{formatRupiah(it.subtotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: "2px solid #334155" }}>
              <td colSpan={4} style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700 }}>Total</td>
              <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "monospace", fontWeight: 700 }}>{formatRupiah(po.total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const BUKTI_MAX_BYTES = 1024 * 1024; // 1 MB, sama dengan batas di backend

function BuktiPembayaran({ po, canUpload, onChanged }) {
  const meta = po.bukti_bayar;
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [proses, setProses] = useState("");
  const [infoKompresi, setInfoKompresi] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!meta) { setUrl(""); return undefined; }
    let objectUrl = "";
    let batal = false;
    (async () => {
      try {
        const { data } = await api.get(`/purchase-orders/${po.id}/bukti-bayar`, { responseType: "blob" });
        if (batal) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      } catch {
        if (!batal) setError("Bukti pembayaran gagal dimuat.");
      }
    })();
    return () => { batal = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [po.id, meta?.diunggah_pada]); // eslint-disable-line react-hooks/exhaustive-deps

  const unggah = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setInfoKompresi("");
    setUploading(true);
    try {
      let berkas = file;
      // Gambar yang kelewat besar dikecilkan otomatis, bukan ditolak.
      if (file.type.startsWith("image/") && file.size > BUKTI_MAX_BYTES) {
        setProses("Mengompres gambar...");
        berkas = await compressImageFile(file, BUKTI_MAX_BYTES);
        if (berkas.size <= BUKTI_MAX_BYTES) {
          setInfoKompresi(`Gambar dikompres otomatis dari ${formatUkuran(file.size)} menjadi ${formatUkuran(berkas.size)}.`);
        }
      }
      if (berkas.size > BUKTI_MAX_BYTES) {
        setError(file.type === "application/pdf"
          ? "Ukuran PDF maksimal 1 MB. Kecilkan berkas terlebih dahulu."
          : "Gambar masih melebihi 1 MB setelah dikompres. Coba unggah foto dengan resolusi lebih kecil.");
        return;
      }
      setProses("Mengunggah...");
      const form = new FormData();
      form.append("file", berkas);
      await api.post(`/purchase-orders/${po.id}/bukti-bayar`, form);
      await onChanged();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); setProses(""); }
  };

  if (!meta && !canUpload) return null;
  const isGambar = (meta?.tipe || "").startsWith("image/");

  return (
    <div className="kedan-card p-5 space-y-3" data-testid="bukti-bayar-card">
      <div className="flex items-center gap-2">
        <Receipt className="w-4 h-4 text-[#045498]" />
        <div className="kedan-label text-slate-500">Bukti Pembayaran</div>
      </div>

      {meta ? (
        <div className="space-y-2">
          {isGambar && url && (
            <a href={url} target="_blank" rel="noreferrer">
              <img src={url} alt="Bukti pembayaran" className="rounded-md border border-slate-200 max-h-56 w-full object-contain bg-slate-50" />
            </a>
          )}
          {!isGambar && url && (
            <a href={url} target="_blank" rel="noreferrer" className="kedan-btn-secondary w-full justify-center" data-testid="buka-bukti">
              <ExternalLink className="w-4 h-4" /> Buka berkas PDF
            </a>
          )}
          <div className="text-xs text-slate-500 leading-relaxed">
            <div className="text-slate-700 font-medium break-all">{meta.nama_file}</div>
            {Math.max(1, Math.round((meta.ukuran || 0) / 1024))} KB · diunggah {formatDateTime(meta.diunggah_pada)}
            {meta.diunggah_oleh && <> oleh {meta.diunggah_oleh}</>}
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-500">Belum ada bukti pembayaran yang diunggah.</p>
      )}

      {canUpload && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={unggah}
            data-testid="input-bukti-bayar"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="kedan-btn-secondary w-full justify-center"
            data-testid="unggah-bukti-btn"
          >
            <Upload className="w-4 h-4" /> {uploading ? (proses || "Memproses...") : meta ? "Ganti Bukti Pembayaran" : "Unggah Bukti Pembayaran"}
          </button>
          <p className="text-xs text-slate-400">
            JPG, PNG, WEBP, atau PDF · maksimal 1 MB. Gambar yang lebih besar dikompres otomatis.
          </p>
          {infoKompresi && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="bukti-kompresi">
              {infoKompresi}
            </div>
          )}
        </>
      )}

      {error && <div className="text-xs text-red-600" data-testid="bukti-error">{error}</div>}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
